/**
 * ExportPanel (005 US2) — exports the marketplace sheet (CSV always, XLSX
 * optional) and the bundle (desktop folder tree / web zip) via PlatformIO.
 *
 * Aurora pass: the finished work reads as a "Mark your selects" review surface —
 * each rendered design is a row with a round check circle (marked = solid accent
 * ✓), a thumbnail/swatch, and a meaningful status badge. "Approve N selects"
 * ships the marked work as a bundle; the sheet exports (CSV/XLSX) live alongside.
 */
import { useEffect, useMemo, useState } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { useBatchStore } from "../stores/batch.js";
import { getExportPrintFiles } from "../stores/exportPrintFiles.js";
import { useSelectionStore } from "../stores/selection.js";
import { buildSheet, toCsvBytes, toXlsxBytes, type SheetRowInput } from "./sheet.js";
import { exportProblemSummary } from "./bundle.js";
import { copyRunTo } from "./copyRun.js";
import { printRefFor } from "./refs.js";
import { readableName } from "../catalog/names.js";
import type { OutputRecord } from "@genz/platform";
import { Icon } from "../ui/Icon.js";
import { BusyButton } from "../ui/BusyButton.js";
import { ActionBar } from "../ui/ActionBar.js";
import { WorkingTimer, formatElapsed } from "../ui/WorkingTimer.js";
import "./export.css";

type RowContext = {
  customer: string;
  product: string;
  phoneModel: string;
  caseStyle: string;
  phoneSlug: string;
  caseStyleSlug: string;
  ext: "jpg" | "png" | "webp";
};

export interface ExportPanelProps {
  /** Display + slug context for the current batch's primary template (fallback). */
  context: RowContext;
}

/**
 * One sheet row per (template × design). When a batch run spans MULTIPLE
 * templates, each output's `templateId` selects that template's own context
 * (phone/case/slug columns) from `ctxByTemplate`; outputs with no matching
 * template (single-template / demo flow) fall back to the panel's `fallback`
 * context. The same design under two templates becomes two correctly-labelled rows.
 */
export function rowsFromOutputs(
  outputs: OutputRecord[],
  fallback: RowContext,
  ctxByTemplate: Map<string, RowContext>,
): SheetRowInput[] {
  const groups = new Map<
    string,
    { ctx: RowContext; designSlug: string; views: SheetRowInput["generatedViews"]; viewRefs: Record<string, string> }
  >();
  for (const o of outputs) {
    const ctx = (o.templateId && ctxByTemplate.get(o.templateId)) || fallback;
    const key = `${o.templateId ?? ""}::${o.designSlug}`;
    const g = groups.get(key);
    if (g) {
      g.views.push(o.view);
      g.viewRefs[o.view] = o.localRef;
    } else {
      groups.set(key, { ctx, designSlug: o.designSlug, views: [o.view], viewRefs: { [o.view]: o.localRef } });
    }
  }
  // WS-Y: no print file is exported, so the sheet must not carry a URL for one. `sheet.ts`
  // already words an absent file as an EMPTY cell rather than a broken ref, which is exactly
  // the right shape here — the marketplace column stays, the promise goes.
  const printFile = getExportPrintFiles();
  return [...groups.values()].map(({ ctx, designSlug, views, viewRefs }) => ({
    customer: ctx.customer,
    product: ctx.product,
    // F-7: the marketplace sheet's `Phone Model` / `Case Style` columns are NAMES. The context
    // now carries resolved names (`catalog/names` via `selectionFromTemplate`); this is the
    // column's own guard, so a stale persisted context or a demo mount still cannot ship a
    // marketplace CSV whose product columns are UUIDs.
    phoneModel: readableName(ctx.phoneModel, { noun: "Model" }),
    caseStyle: readableName(ctx.caseStyle, { noun: "Style" }),
    phoneSlug: ctx.phoneSlug,
    caseStyleSlug: ctx.caseStyleSlug,
    designName: designSlug,
    designSlug,
    ext: ctx.ext,
    generatedViews: views,
    printFile,
    // The sheet's URLs are the ACTUAL written paths (the rendered outputs' own localRefs),
    // so a cell can never point at a path the writer didn't produce. The print cell is
    // derived from them the same way the bundle derives the Print/ file.
    viewRefs: printFile ? { ...viewRefs, print: printRefFrom(viewRefs) } : { ...viewRefs },
  }));
}

/** The print file's ref, derived like `printFilesFromOutputs` so the sheet's Print cell
 *  matches the bundled print file. */
function printRefFrom(viewRefs: Record<string, string>): string {
  const first = Object.entries(viewRefs)[0];
  return first ? printRefFor(first[1], first[0]) : "";
}

export function ExportPanel({ context }: ExportPanelProps) {
  const { io, logger } = useServices();
  const outputs = useBatchStore((s) => s.outputs);
  const batchSelections = useSelectionStore((s) => s.batchSelections);
  const [status, setStatus] = useState<string | null>(null);
  const [copyingSince, setCopyingSince] = useState<number>();
  // "Marked selects" — the rows the operator has approved for ship. Keyed by the
  // same template::design key the sheet groups on, so a design under two templates
  // can be marked independently. Empty by default; the operator opts work in.
  //
  // Held in the batch STORE, not component state: ExportWorkspace mounts this panel only
  // while its sub-tab is active, so a trip to Queue and back silently wiped every mark.
  const markedList = useBatchStore((s) => s.markedSelects);
  const setMarkedSelects = useBatchStore((s) => s.setMarkedSelects);
  const marked = useMemo(() => new Set(markedList), [markedList]);

  // templateId → that template's display/slug context (multi-template runs).
  const ctxByTemplate = useMemo(() => {
    const m = new Map<string, RowContext>();
    for (const s of batchSelections) m.set(s.templateId, s.context);
    return m;
  }, [batchSelections]);

  const rows = useMemo(
    () => rowsFromOutputs(outputs, context, ctxByTemplate),
    [outputs, context, ctxByTemplate],
  );

  // Per-design review rows: one card row per (template × design), carrying the key,
  // a label, the generated-view count + format, and a thumbnail when bytes exist.
  const selects = useMemo(() => {
    const byKey = new Map<string, { key: string; designSlug: string; ctx: RowContext; views: Set<string>; sample?: OutputRecord }>();
    for (const o of outputs) {
      const ctx = (o.templateId && ctxByTemplate.get(o.templateId)) || context;
      const key = `${o.templateId ?? ""}::${o.designSlug}`;
      const g = byKey.get(key);
      if (g) {
        g.views.add(o.view);
        if (!g.sample?.bytes && o.bytes) g.sample = o;
      } else {
        byKey.set(key, { key, designSlug: o.designSlug, ctx, views: new Set([o.view]), sample: o });
      }
    }
    return [...byKey.values()];
  }, [outputs, context, ctxByTemplate]);

  const toggleMark = (key: string) =>
    setMarkedSelects(marked.has(key) ? markedList.filter((k) => k !== key) : [...markedList, key]);
  const rejectMarked = () => setMarkedSelects([]);
  const markedCount = marked.size;

  /** Save one file and report the TRUTH. `PlatformIO.download` returns void by contract, so a
   *  cancelled desktop save dialog resolved normally and the UI said "exported" with nothing on
   *  disk. `downloadResult` (desktop) carries `{cancelled}`; web keeps the plain download. */
  const saveOne = async (name: string, bytes: Uint8Array, mime: string, label: string) => {
    const withResult = io as typeof io & {
      downloadResult?: (n: string, b: Uint8Array, m?: string) => Promise<{ cancelled?: boolean }>;
    };
    if (withResult.downloadResult) {
      const r = await withResult.downloadResult(name, bytes, mime);
      setStatus(r?.cancelled ? `${label} save cancelled` : `${label} exported`);
      return;
    }
    await io.download(name, bytes, mime);
    setStatus(`${label} exported`);
  };

  const exportCsv = () => saveOne("export-sheet.csv", toCsvBytes(rows), "text/csv", "CSV");

  const exportXlsx = () =>
    saveOne(
      "export-sheet.xlsx",
      toXlsxBytes(rows),
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "XLSX",
    );

  /**
   * WS-O / AUD-8 LB-3: "Export full bundle" is GONE.
   *
   * The bundle is already on disk — `export/runWriter.ts` wrote every file into the run folder
   * as its item finished — so a second full write was a no-op that re-marshalled every byte
   * across the IPC bridge. The sheet keeps its own CSV/XLSX verbs, which produce something that
   * does not otherwise exist.
   *
   * "Approve N selects" survives as the SAME copy verb with a filter: it copies the marked rows
   * to a NEW folder the operator picks, read from disk (`copyRunTo`), so a run whose bytes the
   * memory budget dropped is approved in full.
   *
   * No marks is NOT "ship everything": the button used to read "Approve 3 selects" with nothing
   * marked and then quietly bundle all 3 — the action silently widened past what the operator
   * picked. With nothing marked it refuses and says so.
   */
  const approveMarked = async () => {
    if (markedCount === 0) {
      setStatus("Nothing marked — tick the selects you want to approve.");
      return;
    }
    const srcRoot = useBatchStore.getState().lastSavedPath;
    if (!srcRoot) {
      setStatus("This run hasn't written anything yet, so there is nothing to copy.");
      return;
    }
    if (!io.pickSaveDirectory) {
      setStatus("Copying to a second folder needs the desktop app.");
      return;
    }
    const destDir = await io.pickSaveDirectory();
    if (!destDir) {
      setStatus("Copy cancelled — no folder chosen, nothing was written.");
      return;
    }
    const startedAt = Date.now();
    setStatus(null);
    setCopyingSince(startedAt);
    const report = await copyRunTo(io, {
      srcRoot,
      destDir,
      outputs,
      printFiles: useBatchStore.getState().printFiles,
      designs: useBatchStore.getState().designs,
      filter: (o) => marked.has(`${o.templateId ?? ""}::${o.designSlug}`),
      logger,
    }).finally(() => setCopyingSince(undefined));
    setStatus(
      `Approved ${markedCount} select${markedCount === 1 ? "" : "s"} · copied ${report.written} files` +
        `${report.destPath ? ` → ${report.destPath}` : ""}${exportProblemSummary(report)}` +
        ` · took ${formatElapsed(Date.now() - startedAt)}`,
    );
  };

  const sheet = rows.length > 0 ? buildSheet(rows) : null;

  return (
    <section className="export" data-testid="export-panel">
      {sheet ? (
        <>
          {/* R11: this was a bare `<h2>`, the second of three page-title idioms across these
              four surfaces. `.panel-title` is the survivor; the route's one `<h1>` belongs to
              `app/ExportWorkspace.tsx`. */}
          <header className="export__head">
            <div className="export__head-meta">
              <h2 className="panel-title">Mark your selects</h2>
              <p className="panel-sub">
                <strong>{outputs.length}</strong> mockup{outputs.length === 1 ? "" : "s"} across{" "}
                <strong>{rows.length}</strong> design{rows.length === 1 ? "" : "s"} · approve the ones to ship.
              </p>
            </div>
          </header>

          <div className="card card--flush export__selects" data-testid="export-selects">
            <ul className="export__selects-list">
              {selects.map((s) => {
                const isMarked = marked.has(s.key);
                return (
                  <li key={s.key}>
                    <button
                      type="button"
                      className="export__select-row"
                      aria-pressed={isMarked}
                      onClick={() => toggleMark(s.key)}
                      data-testid="export-select-row"
                    >
                      <SelectSwatch output={s.sample} fallback={s.designSlug.slice(0, 2).toUpperCase()} />
                      <span className="export__select-meta">
                        <span className="export__select-name">{s.designSlug}</span>
                        <span className="export__select-sub">
                          {s.ctx.phoneModel} · {s.ctx.caseStyle} · {s.views.size} view{s.views.size === 1 ? "" : "s"} · {s.ctx.ext.toUpperCase()}
                        </span>
                      </span>
                      <span className="export__check" aria-hidden="true"><Icon name="check" size={16} /></span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Rule 7: ONE bar at the bottom of the working column, holding all four verbs the
              surface had spread across a header row and a footer row — and the ONE primary,
              placed last by the primitive. Nothing moved out of reach: Reject marked and the
              two sheet exports are the same controls, in the place the page commits from. */}
          <ActionBar label="Export actions" primary={
            /* The count is the MARKED count, never the row count — the label must name
               exactly what the click will ship. Left clickable at 0 so the reason is
               spoken (a disabled button with no explanation reads as "broken"). */
            <BusyButton
              className="btn btn--primary"
              onAction={approveMarked}
              disabled={outputs.length === 0}
              title={markedCount === 0 ? "Tick at least one select to approve" : undefined}
              data-testid="export-approve-selects"
            >
              Copy {markedCount} selected to…
            </BusyButton>
          }>
            <BusyButton
              className="btn"
              onAction={rejectMarked}
              disabled={markedCount === 0}
              data-testid="export-reject-marked"
            >
              Reject marked
            </BusyButton>
            <BusyButton className="btn" onAction={exportCsv} disabled={outputs.length === 0}
                        data-testid="export-csv-sheet">Export CSV sheet</BusyButton>
            <BusyButton className="btn" onAction={exportXlsx} disabled={outputs.length === 0}
                        data-testid="export-xlsx-sheet">Export XLSX sheet</BusyButton>
          </ActionBar>
          {copyingSince !== undefined && (
            <p className="export__status" data-testid="export-copy-time">
              <WorkingTimer startedAt={copyingSince} label="Copying" />
            </p>
          )}
          {status && <p className="export__status" role="status" data-testid="export-status">{status}</p>}

          <div className="export__sheet">
            <div className="export__sheet-cap">Marketplace sheet preview · {sheet.rows.length} row{sheet.rows.length === 1 ? "" : "s"}</div>
            <div className="export__sheet-scroll table-scroll">
              <table className="table">
                <thead>
                  <tr>{sheet.columns.map((c) => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {sheet.rows.slice(0, 50).map((r, ri) => (
                    <tr key={ri}>
                      {r.cells.map((cell, ci) => (
                        <td key={ci}>{cell || <span className="faint">—</span>}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <EmptyState
          icon="download"
          title="Nothing to export yet"
          hint="Create a batch of mock-ups first — your marketplace sheet and bundle will be ready here."
        />
      )}
    </section>
  );
}

/**
 * A 40×40 review thumbnail: renders the rendered mockup bytes via an object URL
 * (revoked on unmount/replace, mirroring Gallery's Thumb), or a tinted swatch with
 * the design's first glyphs when an output carries no decodable pixels (mock/cloud).
 */
function SelectSwatch({ output, fallback }: { output?: OutputRecord; fallback: string }) {
  const bytes = output?.bytes;
  const isImage = !!bytes && bytes.length > 0;
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (!isImage || typeof URL === "undefined" || !bytes || typeof URL.createObjectURL !== "function") {
      setUrl(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(new Blob([bytes as BlobPart], { type: `image/${output?.format ?? "png"}` }));
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [bytes, isImage, output?.format]);

  return (
    <span className="export__swatch" aria-hidden="true">
      {url ? <img src={url} alt="" /> : fallback}
    </span>
  );
}
